import { ChangeEvent, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { _noop } from 'utils/function.utils';
import styles from './select.module.scss';

type SelectProps = {
  index?: number;
  options?: SelectOption[];
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  config?: SelectData;
  gutterBottom?: boolean;
  gutter?: boolean;
  errorNote?: string;
};

const Select = observer<SelectProps>(
  ({
    index = 0,
    options = [],
    value = '',
    disabled = false,
    onChange = (e) => _noop,
    config,
    gutterBottom = false,
    gutter = false,
    errorNote,
  }: SelectProps) => {
    const store = {
      value: value,
      label: config?.labelText,
      placeholder: config?.placeholderText,
      helpNote: config?.helpNoteText,
      showLabel: config?.labelText && config?.placeholderText?.trim() !== config?.labelText.trim(),
      options: [
        ...(options.length === 0 ||
        options?.[0]?.value !== '' ||
        !options.some((option: SelectOption) => option.value === value)
          ? [{ value: '', text: config?.placeholderText ?? '' }]
          : []),
        ...options,
      ],
      changeValue(newValue: ChangeEvent<HTMLSelectElement>) {
        this.value = newValue.target.value;
        onChange(this.value);
        this.showLabel = true;
      },
      changeInnerValue(newValue: string) {
        this.value = newValue;
      },
      changeOptions(options: SelectOption[]) {
        this.options = options;
      },
    };

    useEffect(() => {
      if (options.length) {
        if (!store.options.some((option: SelectOption) => option.value === value)) {
          store.changeInnerValue('');
        }
        store.changeOptions([
          ...(options.length === 0 ||
          options?.[0]?.value !== '' ||
          !store.options.some((option: SelectOption) => option.value === value)
            ? [{ value: '', text: config?.placeholderText ?? '' }]
            : []),
          ...options,
        ]);
      }
      store.changeInnerValue(value);
    }, [store, options, config, value]);

    return (
      <div
        className={`d-flex align-items-left flex-column ${styles.control} ${
          gutter ? 'cust-my-2' : gutterBottom ? 'cust-mb-2' : ''
        }`}
      >
        {store.showLabel && (
          <label htmlFor={store.label} className={`${styles.controlLabel} ${disabled ? styles.disabled : ''}`}>
            {store.label}
          </label>
        )}
        <select
          className={`form-select w-100 pt-0 ${styles.controlSelect} ${
            store.placeholder ? styles.withPlaceholder : ''
          }`}
          id={store.label}
          // defaultValue={store.value}
          value={store.value}
          onChange={(e) => store.changeValue(e)}
          placeholder={store.placeholder}
          disabled={disabled}
        >
          {store.options?.map((option: SelectOption, _index: number) => (
            <option
              disabled={option?.value === ''}
              hidden={option?.value === ''}
              key={_index.toString() + option.value}
              value={option?.value}
            >
              {option?.text}
            </option>
          ))}
        </select>

        {store.helpNote && <div className={styles.helpNote}>{store.helpNote}</div>}
        {errorNote && errorNote !== '' && <div className={styles.errorNote}>{errorNote}</div>}
      </div>
    );
  }
);

export class SelectData {
  labelText?: string;
  placeholderText?: string;
  helpNoteText?: string;
}

export class SelectOption {
  value!: string;
  text!: string;
}

export default Select;